import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ChevronsUp,
  Crown,
  Globe,
  Heart,
  MessageSquare,
  Smile,
  Timer,
  TrendingUp,
  Users,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/toastContext";
import { StickySaveBar } from "../projects/config/StickySaveBar";
import type { BotsConfig } from "./config/types";
import { listLeaderboards } from "../leaderboards/api";
import {
  botsWarnings,
  hasBotsErrors,
  validateBots,
} from "./settings/botsValidation";
import {
  BOTS_SECTIONS,
  botsSection,
  countBotsChanges,
  isBotsSectionDirty,
  type BotsSectionID,
} from "./settings/sectionMeta";
import { PopulationSection } from "./settings/PopulationSection";
import { LocalesSection } from "./settings/LocalesSection";
import { BackfillSection } from "./settings/BackfillSection";
import { HelpSection } from "./settings/HelpSection";
import { NewcomerSection } from "./settings/NewcomerSection";
import { BehaviourSection } from "./settings/BehaviourSection";
import { ProgressionSection } from "./settings/ProgressionSection";
import { LimitsSection } from "./settings/LimitsSection";
import { LeaderboardsSection } from "./settings/LeaderboardsSection";
import { MaturationSection } from "./settings/MaturationSection";
import { ConfigProblems, ProjectConfigDialogs } from "./ProjectConfigDialogs";
import type { BotSettingsDraft } from "./useBotSettingsDraft";
import styles from "./SettingsTab.module.css";

/** One glyph per rail entry, in the mockup's order. */
const RAIL_ICONS: Record<BotsSectionID, ReactNode> = {
  population: <Users size={15} />,
  locales: <Globe size={15} />,
  backfill: <MessageSquare size={15} />,
  help: <Heart size={15} />,
  newcomer: <Smile size={15} />,
  behaviour: <Activity size={15} />,
  progression: <ChevronsUp size={15} />,
  limits: <Timer size={15} />,
  leaderboards: <Crown size={15} />,
  maturation: <TrendingUp size={15} />,
};

/**
 * `?tab=settings` — the cold-start balance, which used to be one very tall
 * card on the project's Config page.
 *
 * It edits `bot_settings.config` — the simulation's own document since
 * migration 0070, with an ETag of its own — through the Bots page's
 * `useBotSettingsDraft`, the same draft machine the Source tab's Generated form
 * uses: the two forms write one document, and two copies of that machine had
 * already begun to disagree.
 *
 * What changed is the shape: ten cards with an anchor each, a sticky rail of
 * their names, and a fixed save bar. Twenty fields in one scroll with no
 * navigation is what the Config page never had and what this must not become.
 *
 * Two things `bots` holds are NOT edited here. `bots.enabled` is the switch in
 * this page's own header — one boolean with two controls on one screen is a
 * question about which one is winning. `bots.names` and `bots.profile` answer
 * "where does a bot come from" rather than "how does it behave", and live on
 * the Source tab.
 *
 * Mockup: t-103/bots-page-shell.html §4.
 */
export function SettingsTab({
  projectID,
  draft,
}: {
  projectID: string;
  /**
   * The Bots page's ONE draft of the bot settings, owned by the page. It is a
   * prop and not a hook call because this tab is MOUNTED CONDITIONALLY: a
   * draft that lived in here went out with the component the moment somebody
   * clicked «Pool», taking twenty fields of unsaved edits with it and asking
   * nothing — while the same draft installed a beforeunload guard for the
   * browser's own navigation, which is the less likely way to leave.
   */
  draft: BotSettingsDraft;
}) {
  const { push } = useToast();
  const { hash } = useLocation();

  // The leaderboard editor names boards by ext_key and reads their operator,
  // so the tab needs the project's list. A missing list degrades the picker to
  // whatever the config already holds, so the query never blocks the page.
  const { data: boards } = useQuery({
    queryKey: ["project", projectID, "leaderboards", "keys"],
    queryFn: () => listLeaderboards(projectID),
    enabled: !!projectID,
  });

  const { serverSettings, workingSettings, projectConfig, loading } = draft;
  const bots = workingSettings;

  const [activeSection, setActiveSection] = useState<string>(
    BOTS_SECTIONS[0].id,
  );

  const setBots = (patch: Partial<BotsConfig>) =>
    draft.edit((prev: BotsConfig) => ({ ...prev, ...patch }));
  const setBotsIn = <K extends keyof BotsConfig>(
    key: K,
    patch: Partial<BotsConfig[K]>,
  ) =>
    draft.edit((prev: BotsConfig) => ({
      ...prev,
      [key]: { ...(prev[key] as object), ...patch },
    }));

  const { changes, sections } = useMemo(
    () => countBotsChanges(serverSettings, bots),
    [serverSettings, bots],
  );

  // The profile rules are read against the project's own schemas — the second
  // document the server parses these settings against, for the same reason: a
  // cosmetic that fell out of a narrowed allow-list is a rule the backend will
  // refuse. They are edited on the Source tab, but they travel in the same
  // patch, so the save bar has to know about them.
  const botsErrors = useMemo(
    () =>
      validateBots(bots, {
        stats: projectConfig.profile.stats_schema,
        meta: projectConfig.profile.meta_schema,
        // The payload templates are held to the schema of the type they name,
        // which is the same check the server makes of a game's own payload.
        helpTypes: projectConfig.help_request.types,
        boards,
      }),
    [bots, projectConfig.profile, projectConfig.help_request.types, boards],
  );
  const warnings = useMemo(
    () => botsWarnings(bots, projectConfig.chat.history_window),
    [bots, projectConfig.chat.history_window],
  );

  // Scroll-spy — the same idiom the Config page uses for its outline: observe
  // the cards and let the topmost visible one own the rail.
  useEffect(() => {
    if (loading) return;
    if (typeof IntersectionObserver === "undefined") return;
    const els = BOTS_SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => el != null,
    );
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -55% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  // Arriving from a link that names a field — Overview's "Settings →" rows
  // carry `#behaviour`, `#maturation` and the like. Once per hash: the effect
  // re-runs on every re-render of a loaded page, and a card that yanked itself
  // back into view while somebody typed in it would be a bug, not a feature.
  const scrolledTo = useRef("");
  useEffect(() => {
    if (loading) return;
    const target = hash.replace(/^#/, "");
    if (!target || scrolledTo.current === hash) return;
    const el = document.getElementById(target);
    if (!el) return;
    scrolledTo.current = hash;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, loading]);

  /** Is this card's own subsection edited? By id, never by position:
   *  BOTS_SECTIONS is an order somebody is invited to change. */
  const sectionDirty = (id: BotsSectionID) =>
    isBotsSectionDirty(serverSettings, bots, botsSection(id));

  async function doSave() {
    if (hasBotsErrors(botsErrors)) return;
    if (await draft.save()) {
      push({ variant: "success", message: "Config saved." });
    }
  }

  // A read that failed is not a read still running. Saying «Loading…» for it
  // is a spinner nothing ever replaces, on a page whose fields the operator
  // came here to change.
  if (draft.loadError) {
    return (
      <Alert variant="danger" title="Couldn't read the bot settings">
        {draft.loadError}
      </Alert>
    );
  }
  if (loading) return <div>Loading…</div>;

  return (
    <div className={styles.tab}>
      <div className={styles.layout}>
        <nav className={styles.railBox} aria-label="Bots settings sections">
          <div className={styles.railLabel}>Sections</div>
          <div className={styles.railNav}>
            {BOTS_SECTIONS.map((section) => {
              const modified = isBotsSectionDirty(serverSettings, bots, section);
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  title={section.apiName}
                  className={
                    section.id === activeSection
                      ? `${styles.link} ${styles.linkActive}`
                      : styles.link
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(section.id)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <span className={styles.linkIcon} aria-hidden>
                    {RAIL_ICONS[section.id]}
                  </span>
                  <span className={styles.linkLabel}>
                    {section.railLabel ?? section.title}
                  </span>
                  {modified && (
                    <span
                      className={styles.railDot}
                      aria-label="Modified"
                      title="Modified"
                    />
                  )}
                </a>
              );
            })}
          </div>
        </nav>

        <div className={styles.body}>
          {draft.saveError && (
            <ConfigProblems
              title="Couldn't save"
              intro={draft.saveError}
              problems={draft.saveProblems}
            />
          )}

          {botsErrors.problems.length > 0 && (
            <ConfigProblems
              title={`${botsErrors.problems.length} ${
                botsErrors.problems.length === 1 ? "field" : "fields"
              } cannot be saved`}
              problems={botsErrors.problems}
            />
          )}

          <PopulationSection
            value={bots.population}
            onChange={(patch) => setBotsIn("population", patch)}
            dirty={sectionDirty("population")}
            guildMaxMembers={projectConfig.guild.max_members}
          />
          <LocalesSection
            defaultLocale={bots.default_locale}
            localeByCountry={bots.locale_by_country}
            onDefaultLocale={(next) => setBots({ default_locale: next })}
            onLocaleByCountry={(next) => setBots({ locale_by_country: next })}
            dirty={sectionDirty("locales")}
          />
          <BackfillSection
            value={bots.backfill}
            onChange={(patch) => setBotsIn("backfill", patch)}
            dirty={sectionDirty("backfill")}
          />
          <HelpSection
            value={bots.help}
            onChange={(patch) => setBotsIn("help", patch)}
            dirty={sectionDirty("help")}
            // The whole rows, not their names: a type carries a
            // `payload_schema`, and the templates below are held to it.
            helpTypes={projectConfig.help_request.types}
            projectID={projectID}
          />
          <NewcomerSection
            value={bots.newcomer_boost}
            onChange={(patch) => setBotsIn("newcomer_boost", patch)}
            dirty={sectionDirty("newcomer")}
          />
          <BehaviourSection
            simulation={bots.simulation}
            profiles={bots.profiles}
            timeOfDay={bots.time_of_day}
            onSimulation={(patch) => setBotsIn("simulation", patch)}
            onProfiles={(profiles) => setBots({ profiles })}
            onTimeOfDay={(time_of_day) => setBots({ time_of_day })}
            dirty={sectionDirty("behaviour")}
            errors={botsErrors}
            warnings={warnings}
          />
          <ProgressionSection
            profiles={bots.profiles}
            onProfiles={(profiles) => setBots({ profiles })}
            growth={bots.growth}
            onGrowth={(growth) => setBots({ growth })}
            statsSchema={projectConfig.profile.stats_schema}
            statRules={bots.profile.stats}
            leaderboards={bots.leaderboards}
            boards={boards ?? []}
            dirty={sectionDirty("progression")}
            errors={botsErrors}
          />
          <LimitsSection
            guildLimits={bots.guild_limits}
            chat={bots.chat}
            onGuildLimits={(patch) => setBotsIn("guild_limits", patch)}
            onChat={(patch) => setBotsIn("chat", patch)}
            dirty={sectionDirty("limits")}
          />
          <LeaderboardsSection
            rows={bots.leaderboards}
            onChange={(leaderboards) => setBots({ leaderboards })}
            dirty={sectionDirty("leaderboards")}
            boards={boards ?? []}
            growth={bots.growth}
            errors={botsErrors}
          />
          <MaturationSection
            value={bots.maturation}
            onChange={(patch) => setBotsIn("maturation", patch)}
            dirty={sectionDirty("maturation")}
          />

          <Alert variant="info" title="Names and card rules are NOT here">
            <code>bots.names</code> and <code>bots.profile</code> answer «where
            does a bot come from», not «how does it behave» — they live on the{" "}
            <b>Source</b> tab, next to the table that replaces them.
          </Alert>
        </div>
      </div>

      <StickySaveBar
        visible={draft.dirty}
        changes={changes}
        sections={sections}
        disabled={!draft.dirty || hasBotsErrors(botsErrors)}
        // The one problem whose cause is in ANOTHER document, so the reason is
        // said beside the greyed-out button as well as in the banner: the
        // schema a template must satisfy is edited on the Config page, and the
        // row it belongs to may well have scrolled off.
        message={botsErrors.helpPayload}
        saving={draft.saving}
        onDiscard={draft.askDiscard}
        onSave={() => void doSave()}
      />

      <ProjectConfigDialogs
        draft={draft}
        changes={changes}
        sections={sections}
      />
    </div>
  );
}
